import React from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import BackToCockpit from "@/components/BackToCockpit";
import InstantQuoteWidget from "@/components/InstantQuoteWidget";
import { Zap, Shield, Clock } from "lucide-react";

/**
 * /quote — public instant charter quote page.
 * Anonymous visitors get a marketing hero. Logged-in members see the
 * BackToCockpit chip so the flow feels native.
 */
export default function InstantQuote() {
  const { user } = useAuth();

  return (
    <div className="max-w-5xl mx-auto px-6 py-10" data-testid="instant-pj-quote-page">
      {user && <BackToCockpit />}

      {/* Cinematic aviation hero — private jet + sedan at golden hour. Same
          image family as the Empty Legs hero for brand cohesion. Multi-stop
          overlay darkens the bottom-left so the eyebrow + headline stay crisp
          while the composition breathes on the right. */}
      <div
        className="relative overflow-hidden rounded-2xl border border-white/10 -mx-2 md:-mx-4"
        data-testid="instant-pj-quote-hero"
        style={{
          backgroundImage:
            "linear-gradient(115deg, rgba(5,8,16,0.92) 0%, rgba(5,8,16,0.72) 42%, rgba(5,8,16,0.35) 78%, rgba(5,8,16,0.15) 100%), url('https://customer-assets-lqy194kg.emergentagent.net/job_crew-match-6/artifacts/8e8mk2gm_IMG_9331.webp')",
          backgroundSize: "cover",
          backgroundPosition: "center 55%",
        }}
      >
        {/* HUD scanline for editorial/tech cohesion with rest of PJP */}
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.08] mix-blend-overlay"
          style={{
            backgroundImage:
              "repeating-linear-gradient(0deg, rgba(75,156,255,0.6) 0px, rgba(75,156,255,0.6) 1px, transparent 1px, transparent 3px)",
          }}
        />
        <div className="relative px-6 md:px-10 py-10 md:py-16 max-w-3xl">
          <div className="mono text-[10px] tracking-[0.24em] uppercase text-cyan/80">// Instant PJ Quote</div>
          <h1 className="font-display text-4xl md:text-6xl font-bold tracking-tighter leading-[0.95] mt-2 drop-shadow-[0_2px_20px_rgba(0,0,0,0.6)]">
            Price a private jet trip in <span className="text-cyan">seconds</span>.
          </h1>
          <p className="mt-4 text-slate-200 max-w-2xl drop-shadow-[0_1px_8px_rgba(0,0,0,0.7)]">
            Pick a city pair and an aircraft type — we compute the flight time and 2026 market-median
            cost using cruise-speed × hourly rate against the PJP operator fleet, then layer in live
            winds, tri-source fuel pricing, and tanker savings so the number you see is what a savvy
            operator would actually charge.
          </p>
        </div>
      </div>

      <div className="mt-6">
        <InstantQuoteWidget />
      </div>

      <div className="mt-10 grid md:grid-cols-3 gap-4">
        <Bullet icon={Zap}   title="Instant math" body="Great-circle distance, cruise speed, and operator rate — no back-and-forth emails." />
        <Bullet icon={Shield} title="Real operator rates" body="When we know the tail, we use its actual hourly. Otherwise we median across the type." />
        <Bullet icon={Clock}  title="Not a booking" body="This is an estimate. Turn a quote into a real trip via the Charter Request flow." />
      </div>

      {!user && (
        <div className="mt-10 flex flex-wrap gap-3">
          <Link to="/membership" data-testid="quote-cta-join" className="btn-hud btn-hud-solid">JOIN PJP</Link>
          <Link to="/login" data-testid="quote-cta-signin" className="btn-hud">SIGN IN</Link>
        </div>
      )}
    </div>
  );
}

function Bullet({ icon: Icon, title, body }) {
  return (
    <div className="rounded-lg border border-white/10 bg-white/[0.02] p-4">
      <div className="flex items-center gap-2">
        <Icon className="h-4 w-4 text-cyan" />
        <div className="mono text-[10px] tracking-[0.24em] uppercase text-cyan/80">{title}</div>
      </div>
      <div className="text-sm text-white/70 mt-1.5">{body}</div>
    </div>
  );
}
